import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, AlertOctagon, Save } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

export default function ConsoleComplaintForm() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [photoUrls, setPhotoUrls] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/console/orders/${orderId}`);
        setOrder(r.data);
      } catch { toast.error("Couldn't load order."); }
      finally { setLoading(false); }
    })();
  }, [orderId]);

  const submit = async () => {
    if (!details.trim()) { toast.error("Add complaint details first."); return; }
    setBusy(true);
    try {
      await api.post(`/console/orders/${orderId}/complaint`, {
        details,
        admin_notes: adminNotes,
        photo_urls: photoUrls.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Complaint lodged. Order moved to Customer Complaints.");
      navigate("/console/orders/complaints");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't lodge complaint.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!order) return null;

  return (
    <div data-testid="console-complaint-form" className="max-w-2xl">
      <header className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#A33030] font-semibold">Heritage Dahlias · Console</div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1 flex items-center gap-3">
          <AlertOctagon className="w-7 h-7 text-[#A33030]" /> Lodge a complaint
        </h1>
        <div className="mt-3 bg-white border border-[#E8DFCB] rounded p-3 text-sm text-[#3C2A1E]">
          <div className="font-medium">{order.customer_name} , #{order.id?.slice(0, 8)}</div>
          <div className="text-xs text-[#7A6A55]">{order.email} · NZ${Number(order.total || 0).toFixed(2)}</div>
        </div>
      </header>

      <div className="space-y-4">
        <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
          Complaint details
          <textarea rows={5} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="complaint-details" className="mt-1 w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] normal-case tracking-normal font-normal focus:outline-none focus:border-[#A33030]" />
        </label>
        <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
          Photo URLs (one per line, paste from Google Drive / Imgur for now)
          <textarea rows={2} value={photoUrls} onChange={(e) => setPhotoUrls(e.target.value)} data-testid="complaint-photos" className="mt-1 w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] normal-case tracking-normal font-normal focus:outline-none focus:border-[#A33030]" />
        </label>
        <label className="block text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] font-semibold">
          Admin notes
          <textarea rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} data-testid="complaint-notes" className="mt-1 w-full px-3 py-2 bg-white border border-[#E8DFCB] rounded text-sm text-[#3C2A1E] normal-case tracking-normal font-normal focus:outline-none focus:border-[#A33030]" />
        </label>
        <button type="button" onClick={submit} disabled={busy} data-testid="complaint-submit" className="px-5 py-2.5 bg-[#A33030] hover:bg-[#8b2727] text-white rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Lodge complaint
        </button>
      </div>
    </div>
  );
}
